import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { SimpleModal, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { AttachBillSections } from './AttachBillSections';
import { AttachPropertyPicker } from './AttachPropertyPicker';
import useStyles from './attachBillModalStyles';
const DASH = '—';

/**
 * Approve-on-attach (LB-5766): attach a Rejected or Needs-property bill to a property, and in the
 * same gesture teach the system what made it attachable — allow the sender (or their whole domain)
 * and save the account number — so the next bill from this sender never needs a person.
 *
 * A portfolio bill — one statement charging several properties — is attached section by section
 * (LB-5910). The dialog asks the service which sections validation matched, shows those as settled,
 * and asks only about the rest; one property for the whole bill is still how everything else attaches,
 * and what the dialog falls back to when the sections cannot be read.
 *
 * The checkboxes only appear when they would do something: a bill with no sender offers no sender
 * line, a bill with no extracted account number offers no account line. Defaults are deliberately
 * "yes, save these rules" (PRD §6.6) so the system gets smarter with use — the escape hatch is the
 * secondary "attach without saving rules" action, not unticking three boxes.
 */
export const AttachBillModal = observer(_ref => {
  var _state$options, _state$options2, _state$options3;
  let row = _ref.row,
    isOpen = _ref.isOpen,
    handleClose = _ref.handleClose,
    onAttached = _ref.onAttached;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState({
      propertyId: '',
      choices: {},
      serviceType: '',
      allowSender: true,
      allowDomain: false,
      saveAccount: true,
      isSaving: false,
      options: null,
      // The bill the options were loaded for. Until they are, the dialog shows the spinner -- from the
      // very first paint, and when it is reopened for another bill -- rather than one frame of the
      // single picker or of the previous bill's sections.
      loadedFor: null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const set = patch => setState(previous => _objectSpread(_objectSpread({}, previous), patch));
  const billId = row === null || row === void 0 ? void 0 : row.billId;
  const properties = store.company.propertiesList || [];
  // Read by the options effect without re-running it whenever the list is re-read.
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;
  const getBillAttachOptionsAsync = store.companyBills.getBillAttachOptionsAsync;
  useEffect(() => {
    if (!isOpen || !billId) return undefined;
    let cancelled = false;
    // The dialog stays mounted between bills, so every open starts from the defaults: a rule
    // ticked for the last bill (its sender's whole domain, say) must not ride along to this one.
    set({
      options: null,
      loadedFor: null,
      choices: {},
      propertyId: '',
      serviceType: '',
      allowSender: true,
      allowDomain: false,
      saveAccount: true,
      isSaving: false
    });
    getBillAttachOptionsAsync.run({
      billId
    }).then(resp => {
      if (cancelled) return;
      const options = resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response : null;
      // A section somebody chose for on an earlier attach starts with that choice -- when it is a
      // property this person can see. One they cannot would count as chosen behind an empty picker,
      // so that section asks again instead.
      const visible = new Set(propertiesRef.current.map(property => property.propertyId));
      const choices = Object.fromEntries(((options === null || options === void 0 ? void 0 : options.blocks) || []).filter(block => !block.matchedPropertyId && visible.has(block.assignedPropertyId)).map(block => [block.blockIndex, block.assignedPropertyId]));
      set({
        options,
        loadedFor: billId,
        choices
      });
    })
    // A failure the async layer did not swallow (no response at all) still ends the wait: the
    // dialog falls back to one property, as it does for any other failure.
    .catch(() => {
      if (!cancelled) set({
        options: null,
        loadedFor: billId
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, billId, getBillAttachOptionsAsync]);
  const isLoading = state.loadedFor !== billId;
  // A company user or Livable staff is accepted without the list (LB-5970): their address is never offered as an entry.
  const sender = row !== null && row !== void 0 && row.senderAlwaysAccepted ? '' : (row === null || row === void 0 ? void 0 : row.senderEmail) || '';
  const domain = sender.includes('@') ? sender.split('@').pop() : '';
  // The dashboard row carries the RAW extracted number (the mapped table row holds a display dash).
  const rowAccountNumber = row !== null && row !== void 0 && row.accountNumber && row.accountNumber !== DASH ? row.accountNumber : '';
  const propertyNameOf = propertyId => {
    const property = properties.find(candidate => candidate.propertyId === propertyId);
    return (property === null || property === void 0 ? void 0 : property.propertyAddress) || (property === null || property === void 0 ? void 0 : property.propertyName) || 'a property you cannot see';
  };
  const sections = ((_state$options = state.options) === null || _state$options === void 0 ? void 0 : _state$options.blocks) || [];
  const openSections = sections.filter(section => !section.matchedPropertyId);
  // Section by section only when there is something to choose; a bill whose sections all matched was
  // held for another reason, and one property for the bill is the honest question then.
  const bySection = !!((_state$options2 = state.options) !== null && _state$options2 !== void 0 && _state$options2.attachByBlock) && openSections.length > 0;
  const stillToChoose = openSections.filter(section => !state.choices[section.blockIndex]).length;
  const sectionNumbers = openSections.filter(section => section.accountNumber).length;
  // On a portfolio document the bill-level number is the statement's master account, which the service
  // never saves. Offered only when it is a section's own number: the one section this bill narrows to.
  const accountNumber = (_state$options3 = state.options) !== null && _state$options3 !== void 0 && _state$options3.portfolioDocument ? sections.length === 1 && sections[0].accountNumber || '' : rowAccountNumber;
  const selectedProperty = properties.find(property => property.propertyId === state.propertyId);
  // Unique service labels of the chosen property, for scoping the saved account number. Optional:
  // blank means the number covers every service the provider bills for.
  const serviceTypes = [...new Set(((selectedProperty === null || selectedProperty === void 0 ? void 0 : selectedProperty.utilities) || []).map(utility => utility.utilityType).filter(Boolean))];
  const handleChoose = (blockIndex, propertyId) => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
    choices: _objectSpread(_objectSpread({}, previous.choices), {}, {
      [blockIndex]: propertyId
    })
  }));
  const handleUseForRest = propertyId => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
    choices: _objectSpread(_objectSpread({}, previous.choices), Object.fromEntries(openSections.filter(section => !previous.choices[section.blockIndex]).map(section => [section.blockIndex, propertyId])))
  }));
  const attach = async saveRules => {
    var _resp$response;
    const where = bySection ? {
      blocks: openSections.map(section => ({
        blockIndex: section.blockIndex,
        propertyId: state.choices[section.blockIndex]
      }))
    } : {
      propertyId: state.propertyId
    };
    const resp = await store.companyBills.attachBillAsync.run(_objectSpread(_objectSpread({
      billId: row.billId
    }, where), {}, {
      addAllowedSender: saveRules && !!sender && state.allowSender,
      addAllowedSenderDomain: saveRules && !!domain && state.allowDomain,
      saveUtilityAccount: saveRules && state.saveAccount && (bySection ? sectionNumbers > 0 : !!accountNumber),
      serviceType: bySection ? null : state.serviceType || null
    }));
    if (!resp || resp.isSuccessful === false) {
      // Network errors are toasted by the async layer; a 409 answer (wrong status, concurrent
      // change) arrives here with the service's message, which is the actionable part.
      if (resp !== null && resp !== void 0 && resp.message) toast('error', {
        body: [resp.message]
      });
      return;
    }
    const notes = ((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.notes) || [];
    // Every property the bill is now on: the ones chosen, and the ones its matched sections were already on.
    const propertyCount = bySection ? new Set([...Object.values(state.choices), ...sections.filter(section => section.matchedPropertyId).map(section => section.matchedPropertyId)]).size : 1;
    const what = propertyCount > 1 ? "Bill attached to ".concat(propertyCount, " properties") : 'Bill attached';
    toast('success', {
      body: [saveRules ? "".concat(what, " \u2014 rules saved.") : "".concat(what, "."), ...notes]
    });
    onAttached === null || onAttached === void 0 || onAttached();
  };
  const handleAttach = async saveRules => {
    // The row comes from the live list and can vanish under the modal (refresh, delete elsewhere).
    if (!row) return;
    set({
      isSaving: true
    });
    try {
      await attach(saveRules);
    } catch (_unused) {
      // Only a failure the async layer could not answer reaches here (no response at all); it has not
      // been said to anybody yet.
      toast('error', {
        title: 'Bill not attached',
        body: ['The service did not answer. Try again in a moment.']
      });
    } finally {
      // Whatever happened, the buttons come back: a failure the async layer did not swallow must not
      // leave the dialog saying "Attaching…" for ever.
      set({
        isSaving: false
      });
    }
  };
  const canAttach = !!row && !state.isSaving && !isLoading && (bySection ? stillToChoose === 0 : !!state.propertyId);
  const subTitle = isLoading ? undefined : bySection ? "This bill has ".concat(sections.length, " sections, each for a property. We matched ").concat(sections.length - openSections.length, " \u2014 choose the property for ").concat(openSections.length === 1 ? 'the one' : "the ".concat(openSections.length), " we could not.") : 'The bill re-runs against the property you choose. Saved rules make the next bill attach itself.';
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: isOpen
    // Not while an attach is on its way: closing then and opening another bill would cancel it
    // before it could say how it went.
    ,
    handleClose: state.isSaving ? () => {} : handleClose
    // Its own buttons are the dialog's; the modal's footer would add a second Cancel beside them.
    ,
    noFooter: true
    // Neutral until the sections arrive, so it does not flip from one title to the other.
    ,
    title: isLoading ? 'Attach bill' : bySection ? 'Attach to properties' : 'Attach to property',
    subTitle: subTitle
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, isLoading ? /*#__PURE__*/React.createElement("div", {
    className: classes.loading
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 18
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, "Reading the bill's sections\u2026")) : bySection ? /*#__PURE__*/React.createElement(AttachBillSections, {
    sections: sections,
    choices: state.choices,
    properties: properties,
    propertyNameOf: propertyNameOf,
    disabled: state.isSaving,
    onChoose: handleChoose,
    onUseForRest: handleUseForRest
  }) : /*#__PURE__*/React.createElement(AttachPropertyPicker, {
    id: "attach-bill-property",
    properties: properties,
    value: state.propertyId,
    onChange: propertyId => set({
      propertyId,
      serviceType: ''
    }),
    label: "Property",
    placeholder: "Search by address or name",
    disabled: state.isSaving,
    autoFocus: true
  }), !isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.rules
  }, (!!sender || (bySection ? sectionNumbers > 0 : !!accountNumber)) && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.rulesHeading
  }, "So the next bill attaches itself"), !!sender && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.allowSender,
      onChange: event => set({
        allowSender: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Add ".concat(sender, " as an allowed sender")
  }), !!domain && (row === null || row === void 0 ? void 0 : row.senderDomainIsFreeMail) && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, "@".concat(domain, " is a free email provider, so only this address can be allowed \u2014 not the whole domain.")), !!domain && !(row !== null && row !== void 0 && row.senderDomainIsFreeMail) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.allowDomain,
      onChange: event => set({
        allowDomain: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Allow the entire @".concat(domain, " domain")
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, "Use the domain when the provider sends from changing addresses (billing@, no-reply-7@, \u2026)."))), bySection && sectionNumbers > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.saveAccount,
      onChange: event => set({
        saveAccount: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Save each section's account number to its property"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, "Only a number printed on a section is saved, to the property you chose for it. The statement's own account number covers every property, so it is never saved.")), !bySection && !!accountNumber && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.saveAccount,
      onChange: event => set({
        saveAccount: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Save account #".concat(accountNumber, " for this property")
  }), state.saveAccount && /*#__PURE__*/React.createElement(TextField, {
    select: true,
    label: "For service (optional)",
    value: state.serviceType,
    onChange: event => set({
      serviceType: event.target.value
    }),
    helperText: "Leave blank if this number covers every service.",
    disabled: state.isSaving || !state.propertyId,
    className: classes.serviceField
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Every service"), serviceTypes.map(serviceType => /*#__PURE__*/React.createElement(MenuItem, {
    key: serviceType,
    value: serviceType
  }, serviceType))))), /*#__PURE__*/React.createElement("div", {
    className: classes.footer
  }, bySection && stillToChoose > 0 && !isLoading && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.footerHint
  }, "Choose a property for ", stillToChoose === 1 ? '1 more section' : "".concat(stillToChoose, " more sections"), " to attach."), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: !canAttach,
    onClick: () => handleAttach(true)
  }, state.isSaving ? 'Attaching…' : 'Attach & save rules'), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    disabled: !canAttach,
    onClick: () => handleAttach(false)
  }, "Attach without saving rules"), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: state.isSaving,
    onClick: handleClose
  }, "Cancel")))));
});